import { memo } from 'react';
import { Handle, Position, useReactFlow, useNodeConnections } from '@xyflow/react';
import { EcaInlineField } from '../EcaInlineField.jsx';
import { useCatalogue } from '../EcaPluginCatalogueContext.jsx';
import { useNodeHover, TopHandleLabels, nodeShell, NODE_COLORS, useZoomLod, NodeLodOverlay } from '../EcaHandleLabel.jsx';
import { getNodeIcon } from '../ecaNodeVisuals.js';

/**
 * Condition node — evaluates a condition and routes execution.
 *
 * Axes:
 *   - LEFT handle: flux input (blue)
 *   - RIGHT handles × 2: TRUE (top-right, green) and FALSE (bottom-right, red)
 *   - TOP handles: data inputs for wirable config fields (green)
 *   - No BOTTOM data outputs (conditions don't produce tokens)
 */
export const EcaConditionNode = memo(function EcaConditionNode({ id, data, selected }) {
  const { updateNodeData } = useReactFlow();
  const { hovered, hoverProps } = useNodeHover();
  const lod = useZoomLod();
  // Node-scoped subscription: re-renders only when THIS node's connections change.
  const connections = useNodeConnections({ id });
  const { getPluginDef } = useCatalogue();
  const pluginDef = getPluginDef('conditions', data.plugin);
  const configSchema = pluginDef?.configSchema ?? [];
  const wirableFields = configSchema.filter((f) => f.wirable);

  // Keys of handles that have a live edge — their labels stay visible without hover
  const connectedTopKeys = new Set(
    connections.filter(c => c.target === id && c.targetHandle?.startsWith('data-in-'))
      .map(c => c.targetHandle.replace('data-in-', ''))
  );

  const updateConfig = (key, value) =>
    updateNodeData(id, { configuration: { ...data.configuration, [key]: value } });

  return (
    <div {...hoverProps} style={{ overflow: 'visible', position: 'relative' }}>

      {/* TOP data input handles (wirable fields) */}
      {wirableFields.map((field, i) => (
        <Handle
          key={`data-in-${field.key}`}
          type="target"
          position={Position.Top}
          id={`data-in-${field.key}`}
          style={{ left: `${((i + 1) / (wirableFields.length + 1)) * 100}%`, background: '#10B981', border: '2px solid #059669', width: 10, height: 10 }}
        />
      ))}
      {/* LEFT flux input */}
      <Handle type="target" position={Position.Left} id="flux-in"
        style={{ background: '#3B82F6', border: '2px solid #1D4ED8', width: 12, height: 12 }} />

      {/* RIGHT TRUE flux output (upper) */}
      <Handle type="source" position={Position.Right} id="flux-out-true"
        style={{ top: '35%', background: '#22C55E', border: '2px solid #16A34A', width: 12, height: 12 }} />

      {/* RIGHT FALSE flux output (lower) */}
      <Handle type="source" position={Position.Right} id="flux-out-false"
        style={{ top: '65%', background: '#EF4444', border: '2px solid #DC2626', width: 12, height: 12 }} />

      {/* Hover labels — also shown when the handle is connected */}
      <TopHandleLabels items={wirableFields} hovered={hovered} connectedKeys={connectedTopKeys} />

      {/* Visual shell */}
      <div style={{ ...nodeShell(NODE_COLORS.condition, selected), opacity: lod ? 0 : 1, transition: 'opacity 160ms ease' }}>
        {/* Header */}
        <div className="bg-amber-500 text-white px-3 py-1.5 flex items-center gap-2">
          <span className="text-xs font-bold uppercase tracking-wider opacity-80">Condition</span>
          <span className="text-xs font-medium truncate">{data.label}</span>
        </div>

        {/* Plugin badge */}
        <div className="px-3 pt-2 pb-1">
          <span className="text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded border border-amber-200 font-mono">
            {data.plugin}
          </span>
        </div>

        {/* Config fields */}
        <div className="px-3 pb-2 space-y-1">
          {configSchema.map((field) => (
            <EcaInlineField
              key={field.key}
              nodeId={id}
              fieldKey={field.key}
              label={field.label}
              value={data.configuration?.[field.key] ?? ''}
              type={field.type}
              options={field.options}
              placeholder={field.placeholder}
              canBeWired={field.wirable}
              onChange={(val) => updateConfig(field.key, val)}
            />
          ))}
        </div>

        {/* TRUE / FALSE output labels aligned with handles */}
        <div className="border-t border-gray-100 px-3 pb-2 pt-1 flex flex-col gap-1">
          <div className="flex justify-end">
            <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded border border-green-200">TRUE ▶</span>
          </div>
          <div className="flex justify-end">
            <span className="text-xs font-semibold text-red-500 bg-red-50 px-2 py-0.5 rounded border border-red-200">FALSE ▶</span>
          </div>
        </div>
      </div>

      {/* Low-zoom billboard */}
      <NodeLodOverlay
        active={lod} colors={NODE_COLORS.condition} selected={selected}
        icon={getNodeIcon('condition', data.plugin)}
        title={data.label}
      />
    </div>
  );
});
